export type ToastType = "success" | "error" | "info" | "warning";

export interface Toast {
  id: number;
  type: ToastType;

  title?: string;
  description?: string;

  duration: number;
  count: number;
  key: string;

  timeoutId?: ReturnType<typeof setTimeout>;
}

type QueuedToast = Omit<Toast, "id" | "timeoutId">;

export interface ShowToastOptions {
  type?: ToastType;
  title?: string;
  description?: string;
  duration?: number;
  dedupe?: boolean;
  resetOnDedupe?: boolean;
}

const DEFAULT_DURATION = 3000;
const MAX_VISIBLE = 3;

const toastsState = () =>
  useState("app_toasts", () => ({
    active: [] as Toast[],
    queue: [] as QueuedToast[],
    nextId: 0,
  }));

function makeKey(type: ToastType, title?: string, description?: string) {
  return `${type}|${title ?? ""}|${description ?? ""}`;
}

export function useToast() {
  const s = toastsState();

  /**
   * Schedules automatic toast removal
   */
  function schedule(toast: Toast) {
    clearTimeout(toast.timeoutId);

    toast.timeoutId = setTimeout(() => {
      remove(toast.id);
    }, toast.duration);
  }

  /**
   * Fills active slots from queue
   */
  function flushQueue() {
    while (s.value.active.length < MAX_VISIBLE && s.value.queue.length > 0) {
      const next = s.value.queue.shift()!;
      const toast: Toast = {
        id: ++s.value.nextId,
        ...next,
      };
      s.value.active.push(toast);
      schedule(toast);
    }
  }

  /**
   * Removes toast by id
   */
  function remove(id: number) {
    const toast = s.value.active.find(t => t.id === id);
    if (toast?.timeoutId) clearTimeout(toast.timeoutId);

    s.value.active = s.value.active.filter(t => t.id !== id);
    flushQueue();
  }

  /**
   * Finds existing toast for deduplication
   */
  function findDedupe(key: string) {
    const active = s.value.active.find(t => t.key === key);
    if (active) return { where: "active" as const, toast: active };

    const index = s.value.queue.findIndex(t => t.key === key);
    if (index !== -1) return { where: "queue" as const, index };

    return null;
  }

  /**
   * Shows a toast
   */
  function show(options: ShowToastOptions): number {
    const {
      type = "info",
      title,
      description,
      duration = DEFAULT_DURATION,
      dedupe = true,
      resetOnDedupe = true,
    } = options;

    const key = makeKey(type, title, description);

    if (dedupe) {
      const found = findDedupe(key);

      if (found?.where === "active") {
        const toast = found.toast;
        toast.count++;

        if (resetOnDedupe) {
          toast.duration = duration;
          schedule(toast);
        }

        return toast.id;
      }

      if (found?.where === "queue") {
        const toast = s.value.queue[found.index];
        toast.count++;

        if (resetOnDedupe) {
          toast.duration = duration;
        }

        return -1;
      }
    }

    const base: QueuedToast = {
      type,
      title,
      description,
      duration,
      count: 1,
      key,
    };

    if (s.value.active.length < MAX_VISIBLE) {
      const toast: Toast = {
        id: ++s.value.nextId,
        ...base,
      };
      s.value.active.push(toast);
      schedule(toast);
      return toast.id;
    }

    s.value.queue.push(base);
    return -1;
  }

  return {
    toasts: computed(() => s.value.active),
    queuedCount: computed(() => s.value.queue.length),

    show,
    success: (o: Omit<ShowToastOptions, "type">) =>
      show({ ...o, type: "success" }),
    error: (o: Omit<ShowToastOptions, "type">) => show({ ...o, type: "error" }),
    info: (o: Omit<ShowToastOptions, "type">) => show({ ...o, type: "info" }),
    warning: (o: Omit<ShowToastOptions, "type">) =>
      show({ ...o, type: "warning" }),

    remove,
  };
}
